import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import { ReadOnlyField } from "../../../common/u1/ReadOnlyField";
import { useGetCompanyQuery } from "../../../../store/Company/companyApiSlice";
import { EditSectionLayout } from "./EditSectionLayout";
import { EditFooterBar } from "./EditFooterBar";
import { SectionNotice } from "./SectionNotice";
import { SectionStatusPill } from "./SectionStatusPill";
import { SUPPORT_ROUTE } from "./EditNotes";

/* Section 06 — License Information. */

/* Transcribed from LicenseInformation.jsx, in its order. The design lays the
   first six out two-up and gives Activities the full row. */
export const LICENSE_FIELDS = [
  { key: "date_of_establishment", label: "Date of Establishment" },
  { key: "date_of_issue", label: "Date of Issue" },
  { key: "date_of_expiry", label: "Date of Expiry" },
  { key: "license_no", label: "License Number" },
  { key: "registration_body", label: "Registration Body" },
  { key: "legal_form", label: "Legal Form" },
  { key: "activities", label: "Activities (as per license)", full: true },
];

export const LicenseInformationSection = ({
  onNext,
  onPrev,
  onSkip,
  header,
  className = "",
}) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);

  /* Transcribed: the section runs its own company query rather than reading
     the store, and holds the result locally. */
  const { data } = useGetCompanyQuery({
    companyId: currentEditingCompany?.companyID,
    token,
  });
  const [license, setLicense] = useState();

  useEffect(() => {
    if (data) setLicense(data);
  }, [data]);

  const status = currentEditingCompany?.trade_license_status;

  return (
    <EditSectionLayout
      className={className}
      header={header}
      footer={
        /* Continue only moves on — the live onNext is a bare navigation with
           no mutation behind it, because nothing here is editable. */
        <EditFooterBar onBack={onPrev} onSkip={onSkip} onContinue={onNext} />
      }
    >
      {/* `flex-wrap`: with the badge pinned to one line (whitespace-nowrap) a
          narrow column would otherwise push it past the edge. It drops under
          the heading instead, still intact. */}
      <div className="flex flex-wrap items-center gap-2.5">
        <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
          License Information
        </h2>
        {/* "Verified by TREVEX" rather than a bare "Verified": on this section
            the point is who verified it. */}
        <SectionStatusPill
          status={status}
          labels={{ Verified: "Verified by TREVEX" }}
        />
      </div>

      <SectionNotice className="mt-3.5">
        These details were{" "}
        <b className="font-bold">verified by the TREVEX team</b> against your
        official trade license and can&apos;t be edited here. Spotted something
        wrong?{" "}
        {/* A router Link, in THIS tab. */}
        <Link
          to={SUPPORT_ROUTE}
          className="font-semibold text-[var(--coral)] underline decoration-[1.5px] underline-offset-2 hover:text-[var(--coral-deep)]"
        >
          Contact support
        </Link>{" "}
        to request a change.
      </SectionNotice>

      <div className="mt-4 rounded-[14px] border border-[var(--line)] bg-white px-4 py-5 sm:px-6 sm:py-6">
        <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
          {LICENSE_FIELDS.map((field) => (
            <ReadOnlyField
              key={field.key}
              className={field.full ? "sm:col-span-2" : undefined}
              label={field.label}
              labelVariant="field"
              /* Transcribed fallback: a missing value reads as "-". */
              value={license?.[field.key] || "-"}
            />
          ))}
        </div>
      </div>
    </EditSectionLayout>
  );
};

export default LicenseInformationSection;
